/**
 * The **Arena run HUD** (Arena Slice E, PRD #334 / ADR-0177): a fixed bottom-centre
 * vitals gump the runner reads the whole run from at a glance - the survival clock,
 * the health bar, the level + XP bar, and the kill / wave / enemy tallies. The
 * drafted build (base skill + each picked modifier) rides in a sibling `#arena-build`
 * panel pinned bottom-right, shown/hidden in lockstep, so the vitals gump stays tight.
 * The arena is **endless** (ADR-0210), so the clock counts **up** (time survived) -
 * there is no horn to count down to.
 *
 * It is the {@link src/ui/eventBoard.ts storm board}'s contract twin
 * (`init -> {update, sync, reset}`), reading entirely from the server's
 * owner-only {@link ArenaRunStatusMessage} pushes (Slice D, ADR-0176) - pushed on
 * entry, on every beat/bite, and on a heartbeat between - so it reads no world
 * state and sends nothing on the wire. Plain-DOM sibling overlay of the canvas
 * (markup/CSS in `index.html`), `createElement`/`textContent` only, and
 * `pointer-events: none` so it never swallows a tap meant for the battlefield.
 *
 * **Server-anchored, client-ticked** (the storm board's honesty bound): each push
 * carries `elapsedTicks` in 50ms server steps; the HUD anchors a wall-clock start
 * instant and ticks the elapsed time *up* each render frame between pushes,
 * snapping to every fresh push. The one Arena-specific wrinkle is the **draft
 * pause**: while a level-up draft is outstanding the server freezes the run's
 * clock *and* sends no status pushes, so `main.ts` flags {@link setPaused} when
 * the draft overlay opens - the HUD then holds the clock still instead of
 * inventing time the run is not spending. The next push (the post-pick resume)
 * re-anchors and unfreezes.
 *
 * `reset()` is the reconnect guard (the swarm board's PR #243 lesson): a
 * mid-run disconnect forfeits the run server-side and the fresh connection never
 * sends an `active:false` push, so `main.ts` hides the HUD on every Welcome.
 */
import { formatCountdown } from './hud';
import { lowHpState, xpBarState } from './arenaDanger';
import {
  ARENA_COMET_NAME,
  ARENA_FIREBALL_NAME,
  ARENA_SKILL_NAME,
} from './sim/arenaWeapons';
import { MODIFIER_LABELS } from './arenaOverlays';
import type { ArenaRunStatusMessage } from './net/protocol';

/**
 * The length of one server tick in ms - what `elapsedTicks` counts. Must match
 * the server's `STEP_MS`, which the protocol pins at 50ms; duplicated here
 * (rather than imported) so the client bundle never pulls the server module.
 * Pinned by arenaHud.test.ts so a change is a conscious edit.
 */
export const ARENA_TICK_MS = 50;

/**
 * The CSS class carrying the bars' ~150ms ease-out width transition (declared
 * in `index.html`, hit-feedback PRD #430 Slice C / ADR-0224). The fills wear it
 * in the markup so regen ticks and orb streams glide; {@link initArenaHud}
 * drops it for one write (with a forced reflow) whenever a bar legitimately
 * *resets* - the XP level-up rollover, and both bars on a run start - because
 * an eased shrink toward zero would read as losing the vital.
 */
export const ARENA_BAR_EASE_CLASS = 'arena-fill-eased';

/** Options for {@link initArenaHud}; injected by tests, defaulted live. */
export interface ArenaHudOptions {
  /** The document to wire (defaults to the live `document`). */
  doc?: Document;
  /** Wall-clock source for the count-up anchor, defaulting to `performance.now()`. */
  now?: () => number;
}

/** The handle `main.ts` drives the Arena HUD through. */
export interface ArenaHudHandle {
  /**
   * Adopt a fresh owner-only status push: show the HUD on `active: true` (render
   * every field and re-anchor the clock, clearing any draft freeze), hide it on
   * the final `active: false` push.
   */
  update(msg: ArenaRunStatusMessage): void;
  /**
   * Freeze (or unfreeze) the countdown while a level-up draft is outstanding: the
   * server's run clock is frozen during `paused-draft` and no pushes arrive, so
   * ticking locally would invent time. Any fresh push also unfreezes.
   */
  setPaused(paused: boolean): void;
  /**
   * Re-render the elapsed clock from the anchored start instant at the current
   * moment. Called each render frame; a no-op while hidden or draft-frozen.
   */
  sync(): void;
  /**
   * Fire the HUD's level-up celebration (ADR-0218): re-trigger the CSS pop on the
   * level number + XP bar as the draft opens. Purely cosmetic - the class drives
   * one-shot `animation`s in `index.html`; re-adding it (with a reflow between)
   * restarts them, so back-to-back banked drafts each get their own pop.
   */
  celebrate(): void;
  /**
   * Hide the HUD and disarm the clock - the reconnect guard (PR #243): a mid-run
   * disconnect forfeits server-side with no `active:false` push to hide on, so
   * `main.ts` calls this from every Welcome.
   */
  reset(): void;
}

/**
 * The build's HUD rows (fire-mage rework, ADR-0179): the base skill first -
 * named by its current projectile look (the Greater Projectile transformation
 * is the one rename players should see land) - then one row per drafted
 * modifier in the wire's fixed order, labelled like the draft copy.
 */
function buildRows(msg: ArenaRunStatusMessage): string[] {
  // The Comet keystone renames the skill without reskinning the projectile
  // (PRD #419 slice E - the wire kind stays `fireball` at keystone max), so
  // the rename keys off the greater-projectile entry's keystone star, not the
  // projectile look the old Fire Serpent rename used.
  const comet = msg.modifiers.some((m) => m.id === 'greater-projectile' && m.keystone);
  const rows = [
    comet
      ? ARENA_COMET_NAME
      : msg.projectile === 'fireball'
        ? ARENA_FIREBALL_NAME
        : ARENA_SKILL_NAME,
  ];
  for (const entry of msg.modifiers) {
    // A transcended (keystoned) modifier wears its star (PRD #378).
    rows.push(`${MODIFIER_LABELS[entry.id] ?? entry.id} Lv ${entry.level}${entry.keystone ? ' ★' : ''}`);
  }
  return rows;
}

/** Clamp a fill fraction into `[0, 1]` (bad values render empty, never garbage). */
function fillFraction(part: number, whole: number): number {
  if (!Number.isFinite(part) || !Number.isFinite(whole) || whole <= 0) return 0;
  return Math.min(1, Math.max(0, part / whole));
}

/**
 * Write a bar fill's width. Normally the write just lands and the CSS
 * transition ({@link ARENA_BAR_EASE_CLASS}) eases it; a `snap` write is the
 * rollover trick - drop the transition class, set the width, force a reflow so
 * the un-eased width commits, then restore the class - so a reset toward zero
 * never plays as a backward ease, while the very next write eases again.
 */
function setFillWidth(el: HTMLElement | null, width: string, snap: boolean): void {
  if (!el) return;
  if (!snap) {
    el.style.width = width;
    return;
  }
  el.classList.remove(ARENA_BAR_EASE_CLASS);
  el.style.width = width;
  void el.offsetWidth; // force the reflow that commits the transition-less width
  el.classList.add(ARENA_BAR_EASE_CLASS);
}

/**
 * Wire the `#arena-hud` overlay (declared hidden in `index.html`). Every part is
 * looked up once; a missing root or part is tolerated so a partial-markup client
 * never throws - the storm board's resilience rule.
 */
export function initArenaHud(opts: ArenaHudOptions = {}): ArenaHudHandle {
  const doc = opts.doc ?? document;
  const now = opts.now ?? (() => performance.now());
  const root = doc.getElementById('arena-hud');
  const statusRoot = doc.getElementById('arena-status-strip');
  const progressRoot = doc.getElementById('arena-progress-card');
  // The challenge label rides the top-centre status strip (wave · clock ·
  // challenge). Never the `#arena-hud` root: that element is the 7px bottom XP
  // strip, and a block child prepended there pushes the fill row off-screen.
  let challenge = doc.getElementById('arena-hud-challenge');
  if (!challenge && statusRoot) {
    challenge = doc.createElement('span');
    challenge.id = 'arena-hud-challenge';
    statusRoot.append(challenge);
  }
  const clock = doc.getElementById('arena-hud-clock');
  const kills = doc.getElementById('arena-hud-kills');
  const healthFill = doc.getElementById('arena-hud-health-fill');
  const healthText = doc.getElementById('arena-hud-health-text');
  const level = doc.getElementById('arena-hud-level');
  const levelRow = doc.getElementById('arena-hud-level-row');
  const xpFill = doc.getElementById('arena-hud-xp-fill');
  const wave = doc.getElementById('arena-hud-wave');
  const weapons = doc.getElementById('arena-hud-weapons');
  // The drafted-build panel is a sibling gump (bottom-right), no longer a child of
  // #arena-hud, so it is shown/hidden alongside the HUD in lockstep here.
  const buildRoot = doc.getElementById('arena-build');

  /** The wall-clock instant the run's elapsed clock reads zero from, per the latest push. */
  let startedAt = 0;
  let active = false;
  /** Draft-pause freeze: hold the shown clock still (the server's is frozen too). */
  let paused = false;
  /** The elapsed ms shown while frozen (re-rendered verbatim, not extrapolated). */
  let frozenElapsedMs = 0;
  /** The last push's level, for the XP rollover snap; undefined = no run yet. */
  let lastLevel: number | undefined;

  const renderClock = (elapsedMs: number): void => {
    // `formatCountdown` is a pure M:SS formatter of a millisecond span; it reads
    // just as well for elapsed time as for a countdown (endless arena, ADR-0210).
    if (clock) clock.textContent = formatCountdown(Math.max(0, elapsedMs));
  };

  /** Reconcile the build rows in place (reusing `<li>`s, textContent-only). */
  const renderBuild = (msg: ArenaRunStatusMessage): void => {
    if (!weapons) return;
    const rows = buildRows(msg);
    for (const [i, label] of rows.entries()) {
      let li = weapons.children[i] as HTMLLIElement | undefined;
      if (!li) {
        li = doc.createElement('li');
        weapons.appendChild(li);
      }
      if (li.textContent !== label) li.textContent = label;
    }
    while (weapons.childElementCount > rows.length) {
      weapons.removeChild(weapons.lastElementChild!);
    }
  };

  return {
    update(msg) {
      const wasRunning = active;
      active = msg.active;
      if (!msg.active) {
        // The final push: the run is over; the results overlay takes it from here.
        // Run end (survival or death) also releases the low-HP danger pulse.
        lastLevel = undefined;
        if (root) {
          root.hidden = true;
          root.removeAttribute('data-danger');
        }
        if (statusRoot) statusRoot.hidden = true;
        if (progressRoot) progressRoot.hidden = true;
        if (buildRoot) buildRoot.hidden = true;
        return;
      }
      if (challenge) challenge.textContent = msg.challengeName ?? 'Standard';
      // A fresh push is server truth: re-anchor the clock and lift any draft freeze
      // (the post-pick resume push is exactly this).
      paused = false;
      const at = now();
      const elapsedMs = msg.elapsedTicks * ARENA_TICK_MS;
      startedAt = at - elapsedMs;
      renderClock(elapsedMs);
      if (kills) kills.textContent = `${msg.kills} kills`;
      if (healthText) healthText.textContent = `${msg.hp} / ${msg.maxHp}`;
      // The eased bars' rollover guards (PRD #430 Slice C / ADR-0224): a run
      // start (the entry push after town/death/reconnect) snaps BOTH bars to
      // their fresh values, and a level-up snaps the XP bar's reset toward
      // zero - an eased shrink there would read as losing the vital. Every
      // other write eases (regen ticks, orb streams, bites).
      const freshRun = !wasRunning;
      setFillWidth(healthFill, `${Math.round(fillFraction(msg.hp, msg.maxHp) * 100)}%`, freshRun);
      if (level) level.textContent = `${msg.level}`;
      setFillWidth(
        xpFill,
        `${Math.round(fillFraction(msg.xp, msg.xpToNext) * 100)}%`,
        freshRun || (lastLevel !== undefined && msg.level !== lastLevel),
      );
      // The near-level glow (PRD #431 Slice D): light the bar as the next draft
      // approaches, so a pickup stream visibly closes on a ding. One pure
      // decision flips `data-near`; the CSS owns the glow/pulse (index.html). It
      // resets on rollover for free - a level-up zeroes `xp`, so the next push
      // reads below the threshold again.
      if (xpFill) xpFill.toggleAttribute('data-near', xpBarState(msg.xp, msg.xpToNext) === 'near');
      lastLevel = msg.level;
      if (wave) wave.textContent = `Wave ${msg.wave} · ${msg.enemyCount} foes`;
      renderBuild(msg);
      if (root) {
        root.hidden = false;
        // The low-HP danger pulse (PRD #430 Slice D, ADR-0227): one pure
        // decision flips `data-danger`, and the CSS keyframe throb on the
        // health fill rides the attribute (index.html). Level-driven and
        // idempotent - a heal above the threshold releases it here too.
        root.toggleAttribute('data-danger', lowHpState(msg.hp, msg.maxHp) === 'danger');
      }
      if (statusRoot) statusRoot.hidden = false;
      if (progressRoot) progressRoot.hidden = false;
      if (buildRoot) buildRoot.hidden = false;
    },
    celebrate() {
      if (!levelRow) return;
      // Remove + force a reflow + re-add: the standard CSS animation re-trigger,
      // so a banked multi-level draft chain pops once per genuine level.
      levelRow.classList.remove('arena-level-pop');
      progressRoot?.classList.remove('arena-level-pop');
      void (levelRow as HTMLElement).offsetWidth;
      levelRow.classList.add('arena-level-pop');
      progressRoot?.classList.add('arena-level-pop');
    },
    setPaused(next) {
      if (next === paused) return;
      paused = next;
      if (next) {
        // Remember what the clock shows now; sync() re-renders it verbatim until
        // the resume push re-anchors.
        frozenElapsedMs = Math.max(0, now() - startedAt);
      } else {
        // Unfreeze without a push (defensive): resume from the frozen elapsed.
        startedAt = now() - frozenElapsedMs;
      }
    },
    sync() {
      if (!active) return;
      if (paused) {
        renderClock(frozenElapsedMs);
        return;
      }
      renderClock(now() - startedAt);
    },
    reset() {
      // Fresh-connection state: a mid-run disconnect forfeited server-side, so no
      // active:false push is coming - hide unconditionally. A run genuinely still
      // owed to this player does not exist (v1 never resumes a run).
      active = false;
      paused = false;
      lastLevel = undefined;
      if (root) {
        root.hidden = true;
        root.removeAttribute('data-danger');
      }
      if (statusRoot) statusRoot.hidden = true;
      if (progressRoot) progressRoot.hidden = true;
      if (buildRoot) buildRoot.hidden = true;
    },
  };
}
